/**
 * The API paths served by `apps/api` rather than by this app.
 *
 * **128 paths, through tranche 7d.** Every tranche adds its routers here, and
 * this file is the one place that changes.
 *
 * ## Where the rewrite lives — ONE place, and the reason matters
 *
 * `next.config.ts` only, via `apiRewrites()` below. `apps/web/vercel.json`
 * declares no rewrites at all, and `moved-api-paths.guard.test.ts` asserts it
 * stays that way.
 *
 * This docstring used to describe the opposite — a table explaining why the list
 * lived in BOTH `vercel.json` and `next.config.ts`. That design was abandoned
 * after it caused a real incident, and the guard test carries the account:
 * Vercel does not interpolate environment variables into rewrite destinations,
 * so a static `vercel.json` can only ever name ONE origin. It named this
 * branch's preview alias, in 64 entries — which on `main` pointed PRODUCTION web
 * at a feature branch's preview API and, through it, at the development
 * database.
 *
 * So the rewrites moved here, where `API_ORIGIN` answers per environment, and
 * that removed one of the two hand-maintained copies as well. `apiRewrites()`
 * runs in every environment, Vercel included — not only in local dev.
 *
 * On Vercel with paths in this list and no `API_ORIGIN`, it throws and fails the
 * build rather than 404-ing every moved endpoint at runtime. Off Vercel it falls
 * back to `LOCAL_API_ORIGIN`, because `next dev` and `next start` are what
 * `playwright.config.ts` runs.
 *
 * ## The asymmetry that has to be managed, not ignored
 *
 * TX-4875's spike measured this rather than assuming it. `proxy.ts`'s matcher
 * includes `"/(api|trpc)(.*)"` under the comment *"Always run for API routes"*,
 * so a `next.config` rewrite resolves **behind** the middleware — which does two
 * database reads per authenticated navigation and can redirect. The spike's first
 * request through it came back as the `/landing` page.
 *
 * **The resolution: as each tranche adds a path here, it also removes that path
 * from `proxy.ts`'s matcher.** A path proxied to `apps/api` has no business
 * running web's middleware — the API does its own auth, and the middleware's
 * redirects are for page navigations. `PROXY_MATCHER_EXCLUSIONS` below is the
 * record of what has been taken out, and the guard test keeps the two in step by
 * reading `proxy.ts`'s literal matcher source.
 */

/**
 * Path prefixes served by `apps/api`, as Next rewrite sources.
 *
 * Use the `/:path*` suffix form — `next.config.ts` and `vercel.json` both take
 * it, so one string works in both places.
 */
export const MOVED_API_PATHS: readonly string[] = [
  "/api/v1/quotes/:path*",
  "/api/v1/images/:path*",
  "/api/chat-attachment/:path*",
  "/api/v1/weather/:path*",
  "/api/v1/background-images/:path*",
  "/api/v1/cartoons/:path*",
  "/api/v1/affinities/:path*",
  // Settings is split, so these are EXACT paths, never `/api/v1/settings/:path*`.
  // A prefix would take `/v1/settings/version` with it, and that endpoint reports
  // the *web build's* version to web tabs — served from apps/api it would show
  // every signed-in member a refresh prompt that refreshing never clears
  // (TX-4975). The ABSENCE OF `version` here is load-bearing; every other
  // settings route belongs on this list.
  //
  // `tolt-tracking` was missing until the contract suite could not reach it: the
  // route had moved to apps/api, the rewrite had not followed, so a browser
  // request 404'd. Nothing broke in production only because its single caller —
  // `app/(public)/layout.tsx` — fetches the API host directly and never touches
  // a rewrite. That is luck, not design.
  "/api/v1/settings/default-assistant",
  "/api/v1/settings/trial-period",
  "/api/v1/settings/tolt-tracking",
  "/api/v1/settings/require-credit-card-on-onboarding",
  "/api/v1/settings/chat-features",
  "/api/v1/links/:path*",
  "/api/v1/bonus/:path*",
  "/api/v1/desktop/:path*",
  "/api/v1/chat/:path*",
  "/api/v1/admin/chat/:path*",
  "/api/v1/files/:path*",
  "/api/v1/realtime/:path*",
  "/api/v1/payment/:path*",
  "/api/v1/subscription/:path*",
  "/api/v1/admin/quotes/:path*",
  "/api/v1/admin/videos/:path*",
  "/api/v1/admin/distractions/:path*",
  "/api/v1/admin/domains/:path*",
  "/api/v1/admin/cancellation-feedback/:path*",
  "/api/v1/admin/coupons/:path*",
  "/api/v1/admin/plans/:path*",
  "/api/v1/admin/products/:path*",
  "/api/v1/admin/email-logs/:path*",
  "/api/v1/admin/affinities/:path*",
  "/api/v1/admin/coaches/:path*",
  "/api/v1/charts/:path*",
  "/api/v1/admin/charts/:path*",
  "/api/v1/cron/:path*",
  "/api/v1/admin/social/:path*",
  "/api/v1/admin/daily-email-images/:path*",
  "/api/v1/admin/cartoons/:path*",
  "/api/v1/assistant/:path*",
  "/api/v1/coaches/:path*",
  "/api/v1/admin/tts-voices/:path*",
  "/api/v1/admin/assistants/:path*",
  "/api/v1/admin/focus-messages/:path*",
  "/api/v1/tolt/:path*",
  "/api/v1/admin/tolt/:path*",
  // TEMPORARY (TX-4864). Not a moved route — a streaming probe on apps/api,
  // listed here so it is measured through the SAME rewrite every moved route
  // uses. Measuring a hand-made one-off rule would answer a question production
  // does not ask. Remove this with `apps/api/src/routes/health/stream.ts` once
  // the assistant tranche lands.
  "/api/v1/health/:path*",
  "/api/v1/gate",
  // UploadThing (TX-4869). Not under `/v1` — the SDK's client helpers and
  // UploadThing's own callback both address this exact path. Uploads stay
  // same-origin through the rewrite; only the signed callback goes direct to the
  // API domain, via `UPLOADTHING_CALLBACK_URL`.
  "/api/uploadthing",
  // Tranche 2a-iii (TX-4989).
  "/api/v1/user/:path*",
  // Member presence (TX-5329).
  "/api/v1/presence/:path*",
  "/api/v1/tasks/:path*",
  "/api/v1/sessions/:path*",
  "/api/v1/playlists/:path*",
  "/api/v1/daily-summary/:path*",
  "/api/v1/device-link/:path*",
  // Tranche 7d (TX-4992) — the last of the catch-all.
  "/api/v1/admin/users/:path*",
  "/api/v1/admin/assistant-settings/:path*",
  "/api/v1/admin/playlists/:path*",
  "/api/v1/admin/background-images/:path*",
  "/api/v1/admin/analytics/:path*",
  "/api/v1/admin/settings/:path*",
  "/api/v1/admin/tasks/:path*",
  "/api/v1/dev/:path*",
  // Tranche 7d, final router (TX-4992).
  "/api/v1/admin/unread-messages/:path*",
  // Authored onboarding flows (TX-5002).
  "/api/v1/onboarding/:path*",
  "/api/v1/admin/onboarding-flows/:path*",
];

/**
 * Paths removed from `proxy.ts`'s matcher because they are proxied away.
 *
 * Kept separate from `MOVED_API_PATHS` rather than derived: the middleware
 * matcher must be *statically analysable* by Next, so it cannot import this
 * module and compute itself. It is hand-edited, and this list plus the guard test
 * is what stops the two drifting.
 */
export const PROXY_MATCHER_EXCLUSIONS: readonly string[] = [
  "/api/v1/quotes/:path*",
  "/api/v1/images/:path*",
  "/api/chat-attachment/:path*",
  "/api/v1/weather/:path*",
  "/api/v1/background-images/:path*",
  "/api/v1/cartoons/:path*",
  "/api/v1/affinities/:path*",
  // Settings is split, so these are EXACT paths, never `/api/v1/settings/:path*`.
  // A prefix would take `/v1/settings/version` with it, and that endpoint reports
  // the *web build's* version to web tabs — served from apps/api it would show
  // every signed-in member a refresh prompt that refreshing never clears
  // (TX-4975). The ABSENCE OF `version` here is load-bearing; every other
  // settings route belongs on this list.
  //
  // `tolt-tracking` was missing until the contract suite could not reach it: the
  // route had moved to apps/api, the rewrite had not followed, so a browser
  // request 404'd. Nothing broke in production only because its single caller —
  // `app/(public)/layout.tsx` — fetches the API host directly and never touches
  // a rewrite. That is luck, not design.
  "/api/v1/settings/default-assistant",
  "/api/v1/settings/trial-period",
  "/api/v1/settings/tolt-tracking",
  "/api/v1/settings/require-credit-card-on-onboarding",
  "/api/v1/settings/chat-features",
  "/api/v1/links/:path*",
  "/api/v1/bonus/:path*",
  "/api/v1/desktop/:path*",
  "/api/v1/chat/:path*",
  "/api/v1/admin/chat/:path*",
  "/api/v1/files/:path*",
  "/api/v1/realtime/:path*",
  "/api/v1/payment/:path*",
  "/api/v1/subscription/:path*",
  "/api/v1/admin/quotes/:path*",
  "/api/v1/admin/videos/:path*",
  "/api/v1/admin/distractions/:path*",
  "/api/v1/admin/domains/:path*",
  "/api/v1/admin/cancellation-feedback/:path*",
  "/api/v1/admin/coupons/:path*",
  "/api/v1/admin/plans/:path*",
  "/api/v1/admin/products/:path*",
  "/api/v1/admin/email-logs/:path*",
  "/api/v1/admin/affinities/:path*",
  "/api/v1/admin/coaches/:path*",
  "/api/v1/charts/:path*",
  "/api/v1/admin/charts/:path*",
  "/api/v1/cron/:path*",
  "/api/v1/admin/social/:path*",
  "/api/v1/admin/daily-email-images/:path*",
  "/api/v1/admin/cartoons/:path*",
  "/api/v1/assistant/:path*",
  "/api/v1/coaches/:path*",
  "/api/v1/admin/tts-voices/:path*",
  "/api/v1/admin/assistants/:path*",
  "/api/v1/admin/focus-messages/:path*",
  "/api/v1/tolt/:path*",
  "/api/v1/admin/tolt/:path*",
  // TEMPORARY (TX-4864) — see the note on the matching entry above.
  "/api/v1/health/:path*",
  "/api/v1/gate",
  // UploadThing (TX-4869). Not under `/v1` — the SDK's client helpers and
  // UploadThing's own callback both address this exact path. Uploads stay
  // same-origin through the rewrite; only the signed callback goes direct to the
  // API domain, via `UPLOADTHING_CALLBACK_URL`.
  "/api/uploadthing",
  // Tranche 2a-iii (TX-4989).
  "/api/v1/user/:path*",
  // Member presence (TX-5329).
  "/api/v1/presence/:path*",
  "/api/v1/tasks/:path*",
  "/api/v1/sessions/:path*",
  "/api/v1/playlists/:path*",
  "/api/v1/daily-summary/:path*",
  "/api/v1/device-link/:path*",
  // Tranche 7d (TX-4992) — the last of the catch-all.
  "/api/v1/admin/users/:path*",
  "/api/v1/admin/assistant-settings/:path*",
  "/api/v1/admin/playlists/:path*",
  "/api/v1/admin/background-images/:path*",
  "/api/v1/admin/analytics/:path*",
  "/api/v1/admin/settings/:path*",
  "/api/v1/admin/tasks/:path*",
  "/api/v1/dev/:path*",
  // Tranche 7d, final router (TX-4992).
  "/api/v1/admin/unread-messages/:path*",
  // Authored onboarding flows (TX-5002).
  "/api/v1/onboarding/:path*",
  "/api/v1/admin/onboarding-flows/:path*",
];

/**
 * Paths served by `apps/api` that are reached at the API host ONLY.
 *
 * Not rewritten from the web origin, so not in `MOVED_API_PATHS`, and not
 * excluded from `proxy.ts`'s matcher. On web they answer 404.
 *
 * Provider webhooks (TX-5262): Stripe, Clerk and the Vercel drain deliver to
 * `api.thesukha.co` directly. The web copy of the path was what the old
 * dashboard endpoints pointed at, and while it was rewritten, re-enabling one
 * of them would have delivered every event twice.
 *
 * Listed so the contract harness still sends these paths to `apps/api`.
 */
export const API_ONLY_PATHS: readonly string[] = ["/api/v1/webhooks/:path*"];

/** Every path `apps/api` serves, whether or not web rewrites it. */
export const API_SERVED_PATHS: readonly string[] = [...MOVED_API_PATHS, ...API_ONLY_PATHS];

/**
 * The local dev origin of `apps/api`, matching the port `apps/api/dev.ts` binds.
 *
 * https because `dev.ts` reuses the certificate `next dev --experimental-https`
 * generates for the web app: the web app is https locally, and a browser will
 * not call http from an https page — the request is blocked as mixed content.
 */
export const LOCAL_API_ORIGIN = "https://127.0.0.1:3402";

/**
 * Where a moved path is proxied to.
 *
 * Defaults to the local API in development, and that default is load-bearing
 * rather than a convenience. This used to return `undefined` when `API_ORIGIN`
 * was unset, on the reasoning that "a developer with no API running should
 * still get a working web app until a tranche actually moves something".
 *
 * Nine tranches have moved something. `apps/web` is down to 11 routers, so with
 * no origin configured a plain `bun run dev` answers **404 for every moved
 * endpoint** — most of the API. The old fallback protected a state that no
 * longer exists, and its cost is that local development silently stops working
 * unless each developer knows to set a variable nobody told them about.
 *
 * `bun run dev` at the repo root already starts `apps/api` alongside the web app
 * (turbo runs every app's `dev`), so defaulting here is what makes the repo work
 * out of the box without deploying anything.
 */
export function apiOrigin(env: Record<string, string | undefined> = process.env): string | undefined {
  if (env.API_ORIGIN) return env.API_ORIGIN;
  /*
   * On Vercel there is no default and never a guess.
   *
   * `vercel.json` used to carry these rewrites with the origin written into all
   * 64 of them — as a literal `api-git-<branch>-...vercel.app`, this branch's
   * own preview alias. Merged to `main` that would have pointed PRODUCTION web
   * at a feature branch's preview API, which is wired to the development
   * database. A hardcoded origin cannot be right in more than one environment,
   * and this one was not reliably right in any.
   *
   * Vercel does not interpolate environment variables into `vercel.json`
   * rewrite destinations, so the rewrites moved here, where `API_ORIGIN` decides
   * per environment and a deploy needs no code change to point somewhere else.
   */
  if (env.VERCEL) return undefined;
  return LOCAL_API_ORIGIN;
}

/**
 * Rewrite rules for `next.config.ts`, in every environment.
 *
 * This used to return nothing on Vercel, because `vercel.json` resolved these at
 * the edge — cheaper, since the request never had to reach the Next function.
 * That edge rule could only ever name one origin for every environment, and the
 * one it named was a branch preview alias. Correctness wins over the hop:
 * `API_ORIGIN` is read per environment, so preview points at the preview API and
 * production at the production one, from the same commit.
 *
 * The extra hop is small and bounded: `proxy.ts`'s matcher already excludes
 * every moved path, so these requests still skip the middleware and its two
 * database reads.
 *
 * With no `API_ORIGIN` on Vercel this returns nothing rather than guessing, and
 * the moved paths 404 — loudly, at the first request, instead of silently
 * proxying somewhere wrong.
 */
/**
 * Does `apps/api` serve this path?
 *
 * The browser needs this as much as the rewrite does. Once it calls the API
 * directly, a path that did NOT move — `/api/v1/settings/version`, which
 * reports the WEB build's own version — must still go to the web origin, or it
 * 404s against an API that never had it.
 *
 * Derived from `MOVED_API_PATHS` rather than kept as a second list, so the
 * browser, the rewrites and the test harness cannot disagree about who serves
 * what. The harness had its own copy of exactly this; that copy now calls here.
 *
 * @param path a pathname, with or without the `/api` prefix
 */
export function isMovedApiPath(path: string): boolean {
  const pathname = path.split("?")[0] ?? path;

  return MOVED_API_PATHS.some((moved) => {
    // `/api/v1/tasks/:path*` -> `/api/v1/tasks`, and the same without `/api`
    // so a caller that already stripped the base still matches.
    const withApi = moved.replace("/:path*", "");
    const withoutApi = withApi.replace(/^\/api/, "");

    return [withApi, withoutApi].some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
    );
  });
}

export function apiRewrites(
  env: Record<string, string | undefined> = process.env,
  // Injected so a test can exercise the mapping against a stand-in list. With
  // MOVED_API_PATHS empty, asserting against the real one proves nothing — and a
  // test that builds its own expectation and compares it to itself proves less.
  paths: readonly string[] = MOVED_API_PATHS
): Array<{ source: string; destination: string }> {
  const origin = apiOrigin(env);

  /*
   * On Vercel, a missing origin is a failed build — not an empty rewrite list.
   *
   * The old `vercel.json` rewrites were always *present*, just pointed at one
   * hardcoded origin. Configuration can be absent, and absent here is worse than
   * wrong: `apps/web` has no catch-all handler any more, so every moved path
   * 404s. That includes the **Chrome extension**, which calls the web origin
   * (`https://app.thesukha.co/api/v1`) and reaches `apps/api` only through this
   * rewrite — an installed extension cannot be redeployed to work around it.
   *
   * A 404 storm in production is a bad way to learn a variable is missing, so
   * the build stops instead, while someone is still watching.
   */
  if (!origin && env.VERCEL && paths.length > 0) {
    throw new Error(
      "API_ORIGIN is not set.\n\n" +
        `${paths.length} API paths are served by apps/api and are reached through ` +
        "a rewrite from the web origin. Without an origin there is nothing to " +
        "rewrite to, and web has no handler of its own — every one of them would " +
        "404 in production, including the calls installed Chrome extensions make.\n\n" +
        "Set API_ORIGIN on this Vercel project (preview: https://api.thesukha.app, " +
        "production: https://api.thesukha.co)."
    );
  }

  if (!origin) return [];

  /*
   * Every wildcard source also needs its BARE prefix, and leaving it out was a
   * live 404 on the preview.
   *
   * Next matches `/api/v1/tasks/:path*` against `/api/v1/tasks` too, with
   * `:path*` empty — and then renders the destination as
   * `https://api.thesukha.app/api/v1/tasks/`, with a trailing slash. Hono does
   * not route that, so the bare COLLECTION paths 404 while every sub-path keeps
   * working:
   *
   *     /api/v1/tasks/categories   -> :path* = "categories"  -> 200
   *     /api/v1/tasks              -> :path* = ""            -> /api/v1/tasks/ -> 404
   *
   * Which is why it looked like half the app was broken: the tasks list, the
   * playlists list and `PATCH /api/v1/user` all 404'd while the calendar sync,
   * chat channels and task categories were fine.
   *
   * An explicit bare entry, listed BEFORE the wildcard, gives Next an exact
   * match to prefer and forwards the path unchanged.
   */
  return paths.flatMap((source) => {
    const bare = source.endsWith("/:path*") ? source.slice(0, -"/:path*".length) : null;

    return [
      ...(bare ? [{ source: bare, destination: `${origin}${bare}` }] : []),
      { source, destination: `${origin}${source}` },
    ];
  });
}

/**
 * Paths web serves itself that must still skip `proxy.ts`'s middleware — not
 * moved to `apps/api`, so they are not `PROXY_MATCHER_EXCLUSIONS`, whose guard
 * insists every entry is a moved path.
 *
 * `/monitoring` is the Sentry tunnel (`app/monitoring/route.ts`). The
 * middleware's redirects are for page navigations; on an error report from a
 * signed-out visitor it answered 307 → /landing, so Sentry never received it
 * (TX-5465). The guard checks the matcher source excludes each of these too.
 */
export const PROXY_MATCHER_OWN_EXCLUSIONS: readonly string[] = ["/monitoring"];
